// Types for Ingredient Transformation Orders

import type { Ingredient, IngredientUnit } from './ingredient.types';

// Source ingredient for transformation with its quantity
export interface TransformationSourceItem {
  ingredientId: string;
  ingredient?: Ingredient;
  quantity: number;
  unit: IngredientUnit;
  // Note: conversionFactor is now global in TransformationRule, not per source item
}

// Target ingredient for transformation with its quantity
export interface TransformationTargetItem {
  ingredientId: string;
  ingredient?: Ingredient;
  quantity: number;
  unit: IngredientUnit;
  conversionFactor?: number; // Optional: can be calculated from source items
}

export interface TransformationRule {
  id: string;
  name: string;
  description?: string;

  // New: Multiple source and target ingredients (many-to-many)
  sourceItems: TransformationSourceItem[];
  targetItems: TransformationTargetItem[];

  // Legacy fields - mantener para compatibilidad temporal
  sourceIngredientId?: string;
  sourceIngredient?: Ingredient;
  sourceQuantity?: number;
  sourceUnit?: IngredientUnit;
  targetIngredientId?: string;
  targetIngredient?: Ingredient;
  targetQuantity?: number;
  targetUnit?: IngredientUnit;

  conversionFactor: number; // Kept for legacy compatibility
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Source item quantities used in an order
export interface TransformationOrderSourceItem {
  ingredientId: string;
  ingredient?: Ingredient;
  requestedQuantity: number;
  actualQuantity: number;
  unit: IngredientUnit;
  // Note: conversionFactor is retrieved from the parent TransformationRule
}

// Target item quantities produced in an order
export interface TransformationOrderTargetItem {
  ingredientId: string;
  ingredient?: Ingredient;
  expectedQuantity: number;
  actualQuantity: number;
  unit: IngredientUnit;
  efficiency?: number; // Actual efficiency achieved (actualQuantity / expectedQuantity)
}

export interface TransformationOrder {
  id: string;
  orderNumber: string;
  transformationRuleId: string;
  transformationRule?: TransformationRule;
  locationId: string;
  locationName: string;

  // New: Multiple source and target items (many-to-many)
  sourceItems: TransformationOrderSourceItem[];
  targetItems: TransformationOrderTargetItem[];

  // Legacy fields - mantener para compatibilidad temporal
  sourceIngredientId?: string;
  sourceIngredient?: Ingredient;
  requestedSourceQuantity?: number;
  plannedSourceQuantity?: number;
  sourceQuantity?: number;
  actualSourceQuantity?: number;
  targetIngredientId?: string;
  targetIngredient?: Ingredient;
  expectedTargetQuantity?: number;
  actualTargetQuantity?: number;

  efficiency: number; // Overall efficiency percentage
  status: TransformationStatus;
  notes?: string;
  startedAt: string;
  completedAt?: string;
  performedBy: string;
  approvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export type TransformationStatus =
  | 'pending'
  | 'completed'
  | 'cancelled';

export interface TransformationOrderItem {
  id: string;
  orderId: string;
  batchNumber?: string;
  sourceStockBefore: number;
  sourceStockAfter: number;
  targetStockBefore: number;
  targetStockAfter: number;
  transformedAt: string;
  transformedBy: string;
}

export interface TransformationFilters {
  locationId: string;
  status: TransformationStatus | 'all';
  sourceIngredientId: string;
  targetIngredientId: string;
  dateFrom: string;
  dateTo: string;
  performedBy: string;
}

export interface TransformationStats {
  total: number;
  pending: number;
  completed: number;
  cancelled: number;
  totalEfficiency: number;
  totalValueTransformed: number;
}

// Helper functions
export const getTransformationStatusLabel = (status: TransformationStatus): string => {
  const labels = {
    pending: 'En Proceso',
    completed: 'Completada',
    cancelled: 'Cancelada'
  };
  return labels[status];
};

export const getTransformationStatusColor = (status: TransformationStatus): string => {
  const colors = {
    pending: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  return colors[status];
};

export const calculateExpectedOutput = (rule: TransformationRule, inputQuantity: number): number => {
  return inputQuantity * rule.conversionFactor;
};

export const calculateEfficiency = (expected: number, actual: number): number => {
  if (expected === 0) return 0;
  return (actual / expected) * 100;
};

export const validateTransformationStock = (
  sourceIngredient: Ingredient,
  requestedQuantity: number,
  locationId: string
): { isValid: boolean; message?: string; availableStock: number } => {
  const locationStock = sourceIngredient.locationStocks?.find(stock => stock.locationId === locationId);
  const availableStock = locationStock?.currentStock || 0;

  if (availableStock < requestedQuantity) {
    return {
      isValid: false,
      message: `Stock insuficiente. Disponible: ${availableStock} ${sourceIngredient.unit}, Solicitado: ${requestedQuantity} ${sourceIngredient.unit}`,
      availableStock
    };
  }

  return {
    isValid: true,
    availableStock
  };
};

// New: Validate stock for multiple source ingredients
export const validateMultipleSourcesStock = (
  sourceItems: TransformationSourceItem[],
  ingredients: Ingredient[],
  locationId: string
): { isValid: boolean; messages: string[]; invalidItems: string[] } => {
  const messages: string[] = [];
  const invalidItems: string[] = [];

  sourceItems.forEach(sourceItem => {
    const ingredient = ingredients.find(ing => ing.id === sourceItem.ingredientId);
    if (!ingredient) {
      messages.push(`Ingrediente ${sourceItem.ingredientId} no encontrado`);
      invalidItems.push(sourceItem.ingredientId);
      return;
    }

    const validation = validateTransformationStock(ingredient, sourceItem.quantity, locationId);
    if (!validation.isValid) {
      messages.push(`${ingredient.name}: ${validation.message}`);
      invalidItems.push(sourceItem.ingredientId);
    }
  });

  return {
    isValid: invalidItems.length === 0,
    messages,
    invalidItems
  };
};